/**
 * ExitIntentModal — one-time lead-magnet interruption right before the user leaves.
 *
 * Trigger heuristics:
 *   Desktop: mousemove leaves the viewport upward (clientY ≤ 0) after ≥15 s dwell.
 *   Mobile:  fast upward scroll (>800 px/s) after ≥25 s dwell — fallback for touch
 *            devices that never fire mouseleave.
 *
 * Guards (must all hold for the modal to open):
 *   - Not previously shown this browser (localStorage `sm_exit_shown_v1`).
 *   - User has not submitted the lead magnet form.
 *   - At least 15 s of session dwell (avoids fake exits during page load).
 *
 * The primary CTA captures email directly inline and POSTs to the lead-magnet
 * endpoint — one-field interruption that converts higher than redirecting to a form.
 */
import { useEffect, useRef, useState } from "react";
import { X, Sparkles, BookOpen, Download, CheckCircle2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = "sm_exit_shown_v1";
const CONTACTED_KEY = "sm_estimator_submitted";
const GUIDE_KEY = "sm_guide_downloaded";
const MIN_DWELL_MS_DESKTOP = 15_000;
const MIN_DWELL_MS_MOBILE = 25_000;

function alreadyShown() {
  try {
    return localStorage.getItem(STORAGE_KEY) === "1"
      || localStorage.getItem(CONTACTED_KEY) === "1"
      || localStorage.getItem(GUIDE_KEY) === "1";
  } catch {
    return false;
  }
}

function markShown() {
  try {
    localStorage.setItem(STORAGE_KEY, "1");
  } catch {
    /* ignore */
  }
}

function isMobile() {
  if (typeof navigator === "undefined") return false;
  return /Mobi|Android|iPhone|iPad|iPod|Windows Phone/i.test(navigator.userAgent);
}

export default function ExitIntentModal() {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errMsg, setErrMsg] = useState("");
  const armedAtRef = useRef(Date.now());
  const lastScrollRef = useRef({ y: 0, t: 0 });
  const firedRef = useRef(false);

  const trigger = () => {
    if (firedRef.current) return;
    if (alreadyShown()) return;
    // Skip if estimator is already open — user is already converting.
    if (document.querySelector('[data-testid="ai-photo-estimator"]')) return;
    firedRef.current = true;
    setOpen(true);
    markShown();
    // Analytics beacon (best-effort, silent).
    try {
      window.__smTrack?.("exit_intent_shown", window.location.pathname);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    // Only run on landing page. Cheap check — module already imported only there.
    if (alreadyShown()) return;

    const mobile = isMobile();
    const minDwell = mobile ? MIN_DWELL_MS_MOBILE : MIN_DWELL_MS_DESKTOP;

    const onMouseOut = (e) => {
      // Desktop: cursor leaves the viewport upwards.
      if (mobile) return;
      if (Date.now() - armedAtRef.current < minDwell) return;
      const y = e.clientY ?? 0;
      const rel = e.relatedTarget || e.toElement;
      if (y <= 0 && !rel) trigger();
    };

    const onScroll = () => {
      // Mobile fallback: fast upward flick.
      if (!mobile) return;
      const now = performance.now();
      const y = window.scrollY || 0;
      const { y: py, t: pt } = lastScrollRef.current;
      const dy = py - y;      // positive = scrolled up
      const dt = now - pt;    // ms
      lastScrollRef.current = { y, t: now };
      if (dt <= 0 || dt > 400) return;
      if (Date.now() - armedAtRef.current < minDwell) return;
      const vel = (dy / dt) * 1000; // px/s
      if (vel > 800 && y < 400) trigger();
    };

    document.addEventListener("mouseout", onMouseOut);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      document.removeEventListener("mouseout", onMouseOut);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  if (!open) return null;

  const close = () => {
    setOpen(false);
    try {
      window.__smTrack?.("exit_intent_dismissed", "modal");
    } catch {
      /* ignore */
    }
  };

  const submitGuide = async (e) => {
    e.preventDefault();
    if (!email || !name) {
      setErrMsg("Vyplňte prosím meno aj e-mail.");
      setStatus("error");
      return;
    }
    setStatus("loading");
    setErrMsg("");
    try {
      // Smart variant detection: match page URL for renovation keywords.
      const path = typeof window !== "undefined" ? window.location.pathname : "";
      const isReno = /rekonstrukcia|bytov[eo]ho?-jadra|demolacia|1-izboveho?-bytu/i.test(path);
      const variant = isReno ? "rekonstrukcia" : "prvy-dom";
      const res = await fetch(`${API}/api/leads/guide-download`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          source: "exit-intent",
          variant,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      setStatus("success");
      try { localStorage.setItem(GUIDE_KEY, "1"); } catch { /* ignore */ }
      try { window.__smTrack?.("lead_magnet_submit", `${variant}:exit-intent`); } catch { /* ignore */ }
    } catch (err) {
      setStatus("error");
      setErrMsg("Nepodarilo sa odoslať. Skúste znova alebo zavolajte 0907 64 99 64.");
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={close}
      data-testid="exit-intent-modal"
      role="dialog"
      aria-labelledby="exit-intent-title"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative bg-[#0a0a0a] border border-[#00E5FF]/30 rounded-2xl max-w-md w-full overflow-hidden shadow-[0_20px_80px_rgba(0,229,255,0.25)]"
      >
        {/* Accent glow */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#00E5FF]/10 via-transparent to-[#FF5E00]/6 pointer-events-none" />

        <button
          onClick={close}
          aria-label="Zavrieť"
          className="absolute top-3 right-3 z-10 w-9 h-9 flex items-center justify-center rounded-lg hover:bg-white/5 text-zinc-400 hover:text-white transition-colors"
          data-testid="exit-intent-close"
        >
          <X size={18} />
        </button>

        <div className="relative px-6 py-8 sm:px-8 sm:py-10">
          {status === "success" ? (
            <>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 rounded-xl bg-[#00E5FF]/15 border border-[#00E5FF]/40 flex items-center justify-center">
                  <CheckCircle2 size={18} className="text-[#00E5FF]" />
                </div>
                <div className="text-[10px] uppercase tracking-widest text-[#00E5FF] font-black">
                  Hotovo
                </div>
              </div>
              <h3 className="text-2xl font-black tracking-tight leading-tight">
                PDF ide k vám do mailu.
              </h3>
              <p className="text-sm text-zinc-400 mt-3 leading-relaxed">
                Skontrolujte si schránku <strong className="text-white">{email}</strong>.
                Ak PDF neprišiel za 5 minút, pozrite si <strong>spam</strong>.
              </p>
              <a
                href="tel:+421907649964"
                className="mt-6 w-full flex items-center justify-center gap-2 bg-[#00E5FF] hover:brightness-110 text-black font-black py-3.5 rounded-xl transition-all"
                data-testid="exit-intent-success-cta"
              >
                Zavolať teraz: 0907 64 99 64
              </a>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-[#00E5FF]/15 border border-[#00E5FF]/40 flex items-center justify-center">
                  <BookOpen size={14} className="text-[#00E5FF]" />
                </div>
                <div className="text-[10px] uppercase tracking-widest text-[#00E5FF] font-black">
                  Ešte pred odchodom · zdarma PDF
                </div>
              </div>

              <h3
                id="exit-intent-title"
                className="text-2xl sm:text-3xl font-black tracking-tight leading-tight"
              >
                Prvý dom bez chýb — <span className="text-[#00E5FF]">10 vecí</span>, ktoré vám nikto nepovie.
              </h3>
              <p className="text-sm text-zinc-400 mt-3 leading-relaxed">
                15 rokov reálnej skúsenosti z 172+ projektov v Bratislave a Podunajsku.
                <strong className="text-white"> Dostanete PDF do 1 minúty na e-mail.</strong>
              </p>

              <form onSubmit={submitGuide} className="mt-5 space-y-2.5" data-testid="exit-intent-form">
                <input
                  type="text"
                  placeholder="Meno"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full bg-black/40 border border-white/10 focus:border-[#00E5FF]/60 outline-none rounded-xl px-4 py-3 text-white text-sm placeholder:text-zinc-600"
                  data-testid="exit-intent-name"
                />
                <input
                  type="email"
                  placeholder="E-mail"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-black/40 border border-white/10 focus:border-[#00E5FF]/60 outline-none rounded-xl px-4 py-3 text-white text-sm placeholder:text-zinc-600"
                  data-testid="exit-intent-email"
                />
                {status === "error" && errMsg && (
                  <p className="text-xs text-[#FF5E00]">{errMsg}</p>
                )}
                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="w-full flex items-center justify-center gap-2 bg-[#00E5FF] hover:brightness-110 disabled:opacity-60 disabled:cursor-wait text-black font-black py-3.5 rounded-xl transition-all"
                  data-testid="exit-intent-cta"
                >
                  {status === "loading" ? (
                    <>Posielame…</>
                  ) : (
                    <><Download size={16} /> Chcem PDF zdarma <Sparkles size={14} /></>
                  )}
                </button>
              </form>

              <button
                onClick={close}
                className="mt-3 w-full text-center text-xs text-zinc-500 hover:text-zinc-300 py-2 transition-colors"
                data-testid="exit-intent-decline"
              >
                Nie, ďakujem — chcem odísť
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
